(() => {
  class ShippingProtectionOption extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === "true") return;
      this.dataset.bound = "true";
      this.checkbox = this.querySelector(".shipping-protection-option__checkbox");
      this.status = this.querySelector(".shipping-protection-option__status");
      this.checkbox?.addEventListener("change", () => this.updateProtection());
    }

    async updateProtection() {
      if (!this.checkbox || this.getAttribute("aria-busy") === "true") return;

      const shouldAdd = this.checkbox.checked;
      const previousLineKey = this.dataset.lineKey || "";
      const variantId = Number(this.dataset.variantId);
      const scrollPosition = { x: window.scrollX, y: window.scrollY };
      if (!variantId) return;

      this.setBusy(true);
      this.setStatus(shouldAdd ? "Adding protection…" : "Removing protection…");

      try {
        const root = window.Shopify?.routes?.root || "/";
        const sectionIds = this.sectionIds();
        let cartContent;

        if (shouldAdd) {
          const response = await fetch(`${root}cart/add.js`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1 }],
              sections: sectionIds,
              sections_url: window.location.pathname,
            }),
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || "Shipping Protection could not be added.");

          cartContent = await (await fetch(`${root}cart.js`)).json();
          cartContent.sections = result.sections;
          const protectionLine = cartContent.items.find((item) => Number(item.variant_id) === variantId);
          this.dataset.lineKey = protectionLine?.key || "";
        } else {
          if (!previousLineKey) throw new Error("Shipping Protection was not found in the cart.");

          const response = await fetch(`${root}cart/change.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: previousLineKey,
              quantity: 0,
              sections: sectionIds,
              sections_url: window.location.pathname,
            }),
          });
          cartContent = await response.json();
          if (!response.ok) throw new Error(cartContent.description || "Shipping Protection could not be removed.");
          this.dataset.lineKey = "";
        }

        this.updateCartPage(cartContent.sections);

        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "shipping-protection:change",
            cart: cartContent,
          },
        }));
        this.restoreScrollPosition(scrollPosition);
        this.setStatus(shouldAdd ? "Shipping Protection added." : "Shipping Protection removed.");
      } catch (error) {
        this.checkbox.checked = !shouldAdd;
        this.dataset.lineKey = previousLineKey;
        this.setStatus(error instanceof Error ? error.message : "Shipping Protection could not be updated.");
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(isBusy) {
      this.setAttribute("aria-busy", String(isBusy));
      if (this.checkbox) this.checkbox.disabled = isBusy;

      const form = this.closest("form");
      form?.querySelectorAll('[name="checkout"]').forEach((button) => {
        button.disabled = isBusy;
      });
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    sectionIds() {
      const sectionIds = ["cart-drawer"];
      const mainCartSection = document.querySelector(".shopify-section--main-cart");
      const mainCartSectionId = mainCartSection?.id?.replace(/^shopify-section-/, "");
      if (mainCartSectionId) sectionIds.push(mainCartSectionId);
      return sectionIds;
    }

    updateCartPage(sections) {
      const currentSection = document.querySelector(".shopify-section--main-cart");
      const sectionId = currentSection?.id?.replace(/^shopify-section-/, "");
      const updatedSectionHtml = sectionId ? sections?.[sectionId] : null;
      if (!currentSection || !updatedSectionHtml) return;

      const updatedDocument = new DOMParser().parseFromString(updatedSectionHtml, "text/html");
      const currentTotals = currentSection.querySelector(".cart-form__totals");
      const updatedTotals = updatedDocument.querySelector(".cart-form__totals");
      if (currentTotals && updatedTotals) currentTotals.innerHTML = updatedTotals.innerHTML;
    }

    restoreScrollPosition({ x, y }) {
      const restore = () => window.scrollTo(x, y);
      restore();
      window.requestAnimationFrame(() => {
        restore();
        window.requestAnimationFrame(restore);
      });
    }
  }

  function updateVisibleCartCounts(cart) {
    if (!cart?.items) return;

    const visibleItemCount = cart.items
      .filter((item) => item.handle !== "shipping-protection")
      .reduce((count, item) => count + item.quantity, 0);

    window.setTimeout(() => {
      document.querySelectorAll("cart-count").forEach((element) => {
        element.itemCount = visibleItemCount;
      });
    }, 0);
  }

  document.addEventListener("cart:change", (event) => {
    updateVisibleCartCounts(event.detail?.cart);
  });

  async function refreshVisibleCartCounts() {
    try {
      const root = window.Shopify?.routes?.root || "/";
      const cart = await (await fetch(`${root}cart.js`)).json();
      updateVisibleCartCounts(cart);
    } catch {
      // The server-rendered count remains available if the cart request fails.
    }
  }

  if (document.readyState === "complete") {
    refreshVisibleCartCounts();
  } else {
    window.addEventListener("load", refreshVisibleCartCounts, { once: true });
  }

  if (!window.customElements.get("shipping-protection-option")) {
    window.customElements.define("shipping-protection-option", ShippingProtectionOption);
  }
})();
